import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);

  const navItems = [
    { label: 'News', path: '/' },
    { label: 'Connect', path: '/connect' },
    { label: 'Rules', path: '/rules' },
    { label: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => setIsAnimated(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', pt: 8 }}>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              sx={{
                textAlign: 'center',
                py: 2,
                backgroundColor: isActive(item.path) ? 'primary.main' : 'transparent',
                color: isActive(item.path) ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: isActive(item.path) ? 'primary.dark' : 'rgba(13, 71, 161, 0.08)',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontFamily: '"Oswald", sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  fontSize: '1.1rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'white',
          borderBottom: '3px solid',
          borderColor: 'primary.main',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'center',
            py: 1,
            maxWidth: '1400px',
            width: '100%',
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
            position: 'relative',
          }}
        >
          {isMobile ? (
            <>
              <IconButton
                color="primary"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ position: 'absolute', left: 16 }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                {/* Mobile Logo */}
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/1T.png"
                    alt="NGF Logo"
                    sx={{
                      width: 90,
                      height: 90,
                      objectFit: 'contain',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                      },
                    }}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Desktop Navigation - Centered Layout */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                {/* Left Menu Items */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  {navItems.slice(0, 2).map((item, index) => (
                    <Box
                      key={item.label}
                      sx={{
                        transform: isAnimated ? 'translateX(0)' : 'translateX(200px)',
                        opacity: isAnimated ? 1 : 0,
                        transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`,
                      }}
                    >
                      <Button
                        component={Link}
                        to={item.path}
                        sx={{
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          fontWeight: isActive(item.path) ? 700 : 500,
                          fontSize: '1.25rem',
                          px: 2.5,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: isActive(item.path) ? '80%' : '0%',
                            height: '3px',
                            backgroundColor: 'secondary.main',
                            transition: 'width 0.3s ease',
                          },
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'primary.main',
                            '&::after': {
                              width: '80%',
                            },
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    </Box>
                  ))}
                </Box>

                {/* Center Logo */}
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    transform: isAnimated ? 'scale(1)' : 'scale(0)',
                    opacity: isAnimated ? 1 : 0,
                    transition: 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s',
                    mx: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="/1T.png"
                    alt="NGF Logo"
                    sx={{
                      width: 90,
                      height: 90,
                      objectFit: 'contain',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                      },
                    }}
                  />
                </Box>

                {/* Right Menu Items */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  {navItems.slice(2, 4).map((item, index) => (
                    <Box
                      key={item.label}
                      sx={{
                        transform: isAnimated ? 'translateX(0)' : 'translateX(-200px)',
                        opacity: isAnimated ? 1 : 0,
                        transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`,
                      }}
                    >
                      <Button
                        component={Link}
                        to={item.path}
                        sx={{
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          fontWeight: isActive(item.path) ? 700 : 500,
                          fontSize: '1.25rem',
                          px: 2.5,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: isActive(item.path) ? '80%' : '0%',
                            height: '3px',
                            backgroundColor: 'secondary.main',
                            transition: 'width 0.3s ease',
                          },
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'primary.main',
                            '&::after': {
                              width: '80%',
                            },
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar sx={{ mb: 2 }} />
    </>
  );
};

export default Navbar;